import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS } from "@/data/content";
import { useLogo } from "@/context/AssetsContext";

export default function Navbar() {
  const logo = useLogo();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4"
        data-testid="navbar">
        <nav className={`flex w-full max-w-6xl items-center justify-between rounded-2xl px-4 py-2.5 transition-[background,box-shadow,border] duration-500 ${scrolled ? "glass shadow-[0_8px_32px_rgba(0,40,120,0.08)]" : "bg-transparent"}`}>
          <a href="#top" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src={logo} alt="SOSConnect AI" className="h-9 w-9 object-contain" />
            <span className="text-[17px] font-bold tracking-tight">SOSConnect <span className="text-brand">AI</span></span>
          </a>

          <div className="hidden items-center gap-1 lg:flex">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href}
                className="rounded-lg px-3 py-2 text-[13.5px] font-medium text-neutral-600 transition-colors hover:bg-black/5 hover:text-neutral-900"
                data-testid={`nav-link-${l.href.slice(1)}`}>{l.label}</a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <a href="#early-access" className="hidden rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:bg-brand-dark active:scale-95 sm:block" data-testid="nav-cta">
              Get Early Access
            </a>
            <button onClick={() => setOpen((o) => !o)} className="grid h-10 w-10 place-items-center rounded-xl hover:bg-black/5 lg:hidden" data-testid="nav-hamburger" aria-label="Menu">
              <div className="flex flex-col gap-1.5">
                <motion.span animate={open ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }} className="block h-0.5 w-6 bg-neutral-900" />
                <motion.span animate={open ? { opacity: 0 } : { opacity: 1 }} className="block h-0.5 w-6 bg-neutral-900" />
                <motion.span animate={open ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }} className="block h-0.5 w-6 bg-neutral-900" />
              </div>
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-menu">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-md" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
              className="absolute inset-x-4 top-24 rounded-3xl border border-black/5 bg-white p-4 shadow-2xl">
              {NAV_LINKS.map((l, i) => (
                <motion.a key={l.href} href={l.href} onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                  className="block rounded-xl px-4 py-3.5 text-base font-medium text-neutral-800 hover:bg-black/5">{l.label}</motion.a>
              ))}
              <a href="#early-access" onClick={() => setOpen(false)} className="mt-2 block rounded-xl bg-brand px-4 py-3.5 text-center text-base font-semibold text-white">Get Early Access</a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
